import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../lib/api'
import InsightModal from './InsightModal'
import UpgradeModal from './UpgradeModal'
import InsightCard from './InsightCard'
import InsightsUpgradeCard, { MARKET_DEMO_INSIGHTS } from './InsightsUpgradeCard'
import { canViewDetailedInsights } from '../config/features'
import { useDemoMode } from '../hooks/usePreserveQueryParams'
import { demoMarketInsights } from '../utils/demoData'
import { TrendingUp, Target, Sparkles, AlertCircle, MessageCircle, Zap, Star } from 'lucide-react'

interface InsightItem {
  title: string
  summary: string
  details: string
}

interface ParsedInsights {
  summary: string
  whatsWorking: InsightItem[]
  valuePropositions: InsightItem[]
  channels: InsightItem[]
  positioning: InsightItem[]
  recommendations: InsightItem[]
  fullText: string
}

function parseInsights(insights: any): ParsedInsights | null {
  if (!insights || !insights.marketStrategy) return null

  return {
    summary: insights.marketStrategy,
    whatsWorking: insights.whatsWorking || [],
    valuePropositions: insights.valuePropositions?.slice(0, 5) || [],
    channels: insights.communicationChannels?.slice(0, 7) || [],
    positioning: insights.competitivePositioning?.slice(0, 4) || [],
    recommendations: insights.topRecommendations?.slice(0, 4) || [],
    fullText: JSON.stringify(insights, null, 2)
  }
}

export default function MarketIntelligenceInsights() {
  const { activeBrand, currentUser } = useAuth()
  const { isDemoMode } = useDemoMode()
  const [parsedInsights, setParsedInsights] = useState<ParsedInsights | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedInsights, setExpandedInsights] = useState(false)

  // Modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedInsight, setSelectedInsight] = useState<InsightItem | null>(null)
  const [modalIcon, setModalIcon] = useState<React.ReactNode>(null)
  const [modalIconColor, setModalIconColor] = useState('text-indigo-600')
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)

  // Check if user has access to AI Insights (PRO only)
  const hasInsightsAccess = activeBrand?.subscriptionProduct === 'PRO'

  const openModal = (insight: InsightItem, icon: React.ReactNode, iconColor: string) => {
    const subscriptionTier = currentUser?.subscriptionTier
    const access = canViewDetailedInsights(subscriptionTier)

    if (access.allowed) {
      setSelectedInsight(insight)
      setModalIcon(icon)
      setModalIconColor(iconColor)
      setModalOpen(true)
    } else {
      setShowUpgradeModal(true)
    }
  }

  useEffect(() => {
    const brandId = activeBrand?.brandId || activeBrand?.id
    if (brandId && hasInsightsAccess) {
      fetchInsights()
    } else {
      setLoading(false)
    }
  }, [activeBrand?.brandId, activeBrand?.id, hasInsightsAccess])

  const fetchInsights = async () => {
    const brandId = activeBrand?.brandId || activeBrand?.id
    if (!brandId) return

    try {
      setLoading(true)
      setError(null)

      // Use demo data in demo mode
      if (isDemoMode) {
        setParsedInsights(parseInsights(demoMarketInsights))
        setLoading(false)
        return
      }

      const response = await api.get(`/brand-analysis/${brandId}/ai-insights?context=market`)

      if (response.data.success) {
        setParsedInsights(parseInsights(response.data.insights))
      }
    } catch (error: any) {
      console.error('Failed to fetch market insights:', error)
      // Show "generating" state for 404 (insights not generated yet)
      if (error.response?.status === 404) {
        setError('pending')
      } else {
        setError('Unable to load market insights summary')
      }
    } finally {
      setLoading(false)
    }
  }

  // Upgrade prompt for STARTER/TRIAL users
  if (!hasInsightsAccess) {
    return (
      <InsightsUpgradeCard
        title="Market Intelligence Insights"
        description="Get AI-powered market positioning and competitive differentiation strategies"
        demoInsights={MARKET_DEMO_INSIGHTS}
      />
    )
  }

  return (
    <div className="space-y-6">
      {/* Insights Being Generated State */}
      {!loading && !parsedInsights && error === 'pending' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <h5 className="font-semibold text-blue-900 mb-1">Insights Being Generated</h5>
              <p className="text-sm text-blue-800">
                Your personalized market intelligence insights are being generated. This happens automatically during onboarding. Check back soon!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error State */}
      {!loading && error && error !== 'pending' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h5 className="font-semibold text-red-900 mb-1">Unable to Load Insights</h5>
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Market Intelligence Insights Overview */}
      {!loading && parsedInsights && (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg shadow border border-indigo-100 p-6 relative">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-indigo-600" />
              <h3 className="text-lg font-bold text-gray-900">Insights & Recommendations</h3>
            </div>
          </div>

          <div className={`overflow-hidden transition-all ${expandedInsights ? 'max-h-none' : 'max-h-48'}`}>
            {/* Market Positioning Strategy */}
            <div className="mb-6">
              <p className="text-sm font-semibold text-gray-800 mb-2">Market Positioning Strategy</p>
              <p className="text-sm text-gray-700 leading-relaxed">
                {parsedInsights.summary}
              </p>
            </div>

            {/* What's Working Section */}
            {parsedInsights.whatsWorking && parsedInsights.whatsWorking.length > 0 && (
              <div className="mb-6 bg-white rounded-lg p-4 border border-blue-200">
                <div className="flex items-center gap-2 mb-3">
                  <Star className="w-4 h-4 text-blue-600" />
                  <h4 className="font-semibold text-gray-900 text-sm">What's Working</h4>
                </div>
                <div className="space-y-2">
                  {parsedInsights.whatsWorking.map((item, index) => (
                    <InsightCard
                      key={index}
                      title={item.title}
                      summary={item.summary}
                      accentColor="text-blue-600"
                      borderColor="border-blue-300"
                      bgColor="bg-blue-50"
                      onClick={() => openModal(item, <Star className="w-6 h-6" />, 'text-blue-600')}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Value Propositions */}
              {parsedInsights.valuePropositions.length > 0 && (
                <div className="bg-white rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    <h4 className="font-semibold text-gray-900 text-sm">Consumer Value Propositions</h4>
                  </div>
                  <div className="space-y-2">
                    {parsedInsights.valuePropositions.map((prop, index) => (
                      <InsightCard
                        key={index}
                        title={prop.title}
                        summary={prop.summary}
                        accentColor="text-green-600"
                        borderColor="border-green-300"
                        bgColor="bg-green-50"
                        onClick={() => openModal(prop, <TrendingUp className="w-6 h-6" />, 'text-green-600')}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Communication Channels */}
              {parsedInsights.channels.length > 0 && (
                <div className="bg-white rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageCircle className="w-4 h-4 text-blue-600" />
                    <h4 className="font-semibold text-gray-900 text-sm">Communication Channels</h4>
                  </div>
                  <div className="space-y-2">
                    {parsedInsights.channels.map((channel, index) => (
                      <InsightCard
                        key={index}
                        title={channel.title}
                        summary={channel.summary}
                        accentColor="text-blue-600"
                        borderColor="border-blue-300"
                        bgColor="bg-blue-50"
                        onClick={() => openModal(channel, <MessageCircle className="w-6 h-6" />, 'text-blue-600')}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Competitive Differentiation */}
              {parsedInsights.positioning.length > 0 && (
                <div className="bg-white rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="w-4 h-4 text-orange-600" />
                    <h4 className="font-semibold text-gray-900 text-sm">Competitive Differentiation</h4>
                  </div>
                  <div className="space-y-2">
                    {parsedInsights.positioning.map((insight, index) => (
                      <InsightCard
                        key={index}
                        title={insight.title}
                        summary={insight.summary}
                        accentColor="text-orange-600"
                        borderColor="border-orange-300"
                        bgColor="bg-orange-50"
                        onClick={() => openModal(insight, <Target className="w-6 h-6" />, 'text-orange-600')}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Top Strategic Recommendations */}
              {parsedInsights.recommendations.length > 0 && (
                <div className="bg-white rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Zap className="w-4 h-4 text-purple-600" />
                    <h4 className="font-semibold text-gray-900 text-sm">Priority Actions</h4>
                  </div>
                  <div className="space-y-2">
                    {parsedInsights.recommendations.map((rec, index) => (
                      <InsightCard
                        key={index}
                        title={rec.title}
                        summary={rec.summary}
                        accentColor="text-purple-600"
                        borderColor="border-purple-300"
                        bgColor="bg-purple-50"
                        onClick={() => openModal(rec, <Zap className="w-6 h-6" />, 'text-purple-600')}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Expand/Collapse Button - Bottom Center */}
          {!expandedInsights && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
              <button
                onClick={() => setExpandedInsights(true)}
                className="bg-white text-indigo-600 hover:text-indigo-700 px-4 py-2 rounded-2xl shadow font-medium text-sm border border-indigo-200 hover:shadow-md transition-all"
              >
                Expand
              </button>
            </div>
          )}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg shadow border border-indigo-100 p-8">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="relative">
              <Sparkles className="w-12 h-12 text-indigo-600 animate-pulse" />
              <div className="absolute inset-0 w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Analyzing to generate insights & recommendations</h3>
              <p className="text-sm text-gray-600">This may take a few moments...</p>
            </div>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Market Intelligence Recommendations List
      <RecommendationsList />
       */}

      {/* Insight Detail Modal */}
      {selectedInsight && (
        <InsightModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title={selectedInsight.title}
          summary={selectedInsight.summary}
          details={selectedInsight.details}
          icon={modalIcon}
          iconColor={modalIconColor}
        />
      )}

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="detailed market intelligence insights and strategic recommendations"
      />
    </div>
  )
}
